<template>
  <GroupsItemParamValueDropdown
    v-if="isDropdownField"
    ref="inputRef"
    v-model="modelValue"
    :comparison
    :field-settings
    :readonly
    :filter-context
  />
  <GroupsItemParamValueDate
    v-else-if="isDateField"
    ref="inputRef"
    v-model="modelValue"
    :readonly
  />
  <GroupsItemParamValueAmount
    v-else-if="isAmountField"
    ref="inputRef"
    :model-value="modelValue"
    :readonly
    @update:model-value="handleUpdateModelValue"
  />
  <GroupsItemParamValueInput
    v-else-if="isFieldVisible"
    ref="inputRef"
    :model-value="modelValue"
    :readonly
    @update:model-value="handleUpdateModelValue"
  />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useDebounceFn } from '@vueuse/core'

import { FilterComparison } from '../utils/enums'
import { Filter, FilterField } from '../utils/types'

import { DEBOUNCE_DELAY } from '@/const'

import GroupsItemParamValueDate from './GroupsItemParamValueDate.vue'
import GroupsItemParamValueAmount from './GroupsItemParamValueAmount.vue'
import GroupsItemParamValueDropdown from './GroupsItemParamValueDropdown.vue'
import GroupsItemParamValueInput from './GroupsItemParamValueInput.vue'

type Props = {
  fieldSettings?: FilterField
  comparison?: FilterComparison
  readonly?: boolean
  filterContext?: Filter
}

const props = defineProps<Props>()

defineExpose({
  start() {
    inputRef.value?.start()
  },
  initData() {
    inputRef.value?.init?.()
  },
})

const modelValue = defineModel<any>()

const inputRef = ref<
  | typeof GroupsItemParamValueDate
  | typeof GroupsItemParamValueDropdown
  | typeof GroupsItemParamValueAmount
  | typeof GroupsItemParamValueInput
  | null
>(null)

const isDropdownField = computed(() => {
  if (!props.comparison || !props.fieldSettings) return false
  if (props.comparison === FilterComparison.LAST) return true
  if (
    (props.fieldSettings.list || props.fieldSettings.getter) &&
    [FilterComparison.EQ, FilterComparison.NOTEQ].includes(props.comparison)
  )
    return true
  return false
})

const isDateField = computed(() => {
  return isFieldVisible.value && props.fieldSettings?.isDate
})

const isAmountField = computed(() => {
  return isFieldVisible.value && props.fieldSettings?.isAmount
})

const isFieldVisible = computed(
  () =>
    props.comparison &&
    ![FilterComparison.EMPTY, FilterComparison.NOTEMPTY].includes(
      props.comparison,
    ),
)

const handleUpdateModelValue = useDebounceFn((value: any) => {
  modelValue.value = value
}, DEBOUNCE_DELAY * 2)
</script>
